<script setup> 
    // Imports
    import { ref, onMounted, onUnmounted, defineProps } from 'vue';
    import { useRoute } from 'vue-router';
    import { Action } from '@/api/action';
    import Data from '@/api/data';
    import DefaultLayout from '@/layouts/Layout.vue';
    import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';
    import TdwlbConfig from '@/components/modals/TdwlbConfig.vue';
    import { TrashIcon} from '@heroicons/vue/24/solid';

    // When embedded in DashboardPage, we don't wrap in DefaultLayout (parent already provides it)
    defineProps({
        embedded: { type: Boolean, default: false }
    });

    // References
    const loading = ref(true);
    const errorMessage = ref("");
    const loadingRemove = ref(null);
    const config = ref(null);
    const bluetoothDevices = ref([]);
    const modalOpen = ref(false);
    const modalDetails = ref(null);
    
    // Variables
    let requestID = null;
    let resultStream = null;
    let updatedConfig = null;
    const route = useRoute();
    const dataID = route.params.dataID;

    // Lifecycle Hooks
    onMounted(async () => {
        setupListener();
        const response = await Action.get(`?dataID=${dataID}&actionClass=Tdwlb&payload=ListConfigInfo`);
        if (response.status === 'success') {
            requestID = response.details.action_id;
        } else {
            console.log(response);
        }
    });

    onUnmounted(() => {
        resultStream.close();
    });

    // Methods
    const setupListener = async () => {
        loading.value = true;
        errorMessage.value = "";
        
        // Set up response Stream
        resultStream = await Data.stream(dataID, 'action_result');

        resultStream.addEventListener('added', async (event) => {
            const addedData = JSON.parse(event.data);
            for (const result of addedData) {
                if (result.action_class === 'Tdwlb' && result.id === requestID) {
                    if (result.status === 'OK' && result.action_type === 'Read') {
                        // Parse the payload if it's a string, otherwise use it as is
                        config.value = JSON.parse(result.payload);
                        bluetoothDevices.value = config.value.serial_number_list;
                        loading.value = false;
                    } else if (result.status === 'OK' && result.action_type === 'Update') {
                        loadingRemove.value = null;
                        config.value = updatedConfig;
                        bluetoothDevices.value = config.value.serial_number_list;

                        // Reset after a short delay
                        setTimeout(() => {
                            modalOpen.value = false;
                            modalDetails.value = null;
                        }, 100);
                        
                    }
                }
            }
        });

        if (loading.value) {
            setTimeout(() => {
                errorMessage.value = "Failed to connect";
            }, 30000);
        }
    };

    const handleConfigChange = async (newDetails) => {
        updatedConfig = config.value;

        const device = updatedConfig.serial_number_list.find(serial_number => serial_number.serial_number === newDetails.serial_number);
        
        if (device) {
            device.offset = newDetails.offset;
            device.update_frequency = newDetails.update_frequency;
            device.report_interval = newDetails.report_interval;
        } else {
            updatedConfig.serial_number_list.push(newDetails);
        }

        const requestBody = JSON.stringify({
            dataID: dataID,
            actionClass: 'Tdwlb',
            payload: {
                UpdateConfig: JSON.stringify(updatedConfig)
            }
        });

        const response = await Action.put('', requestBody);

        if (response.status === 'success') {
            requestID = response.details.action_id;
        } else {
            console.error('Failed to update config');
        }
    };

    const handleRemoveDevice = async (serial_number) => {
        loadingRemove.value = serial_number;
        updatedConfig = config.value;
        updatedConfig.serial_number_list = updatedConfig.serial_number_list.filter(device => device.serial_number !== serial_number);

        const requestBody = JSON.stringify({
            dataID: dataID,
            actionClass: 'Tdwlb',
            payload: {
                UpdateConfig: JSON.stringify(updatedConfig)
            }
        });

        const response = await Action.put('', requestBody);

        if (response.status === 'success') {
            requestID = response.details.action_id;
        } else {
            console.error('Failed to update config');
        }
    };

</script>

<template>
    <DefaultLayout
        v-if="!embedded"
        :dataID="dataID"
    >
        <div v-if="loading" class="flex flex-col items-center mt-[100px] h-full w-full">
            <h2 class="text-xl font-semibold text-gray-700 mb-8 underline">Please make sure the XetaLynk™ gateway is turned on</h2>
            <LoadingAnimation3 v-if="errorMessage === ''" size="100px" message="Requesting TDWLB Information..." />
            <div v-else class="flex flex-col items-center">
                <h2 class="text-sm text-gray-700">{{ errorMessage }}</h2>
                <button class="default-button mt-4" @click="setupListener()">Try Again</button>
            </div>
        </div>
        <div v-else class="mt-[50px] flex flex-col items-center">
            <button class="default-button mb-8" @click="modalOpen = true; modalDetails = null">Add sensor</button>
            <h2 v-if="bluetoothDevices.length === 0" class="text-xl font-semibold text-gray-700 mb-8">No bluetooth devices connected</h2>
            
            <div v-else class="grid grid-cols-1 gap-8 w-fit">
                <div v-for="device in bluetoothDevices" :key="device.serial_number" class="bg-gray-50 rounded-lg p-6">
                    <div class="flex items-center justify-between">
                        <TrashIcon v-if="loadingRemove !== device.serial_number" class="w-6 h-6 text-red-500 cursor-pointer mr-4" @click="handleRemoveDevice(device.serial_number)" />
                        <LoadingAnimation3 v-else size="20px" />
                        <h2 class="text-xl font-semibold text-gray-700">Serial Num: {{ device.serial_number }}</h2>
                    </div>
                    <img src="@/assets/images/tdwlb_pressure_sensor.png" alt="Tdwlb Sensor" class="w-[200px] cursor-pointer" @click="modalOpen = true; modalDetails = device">
                </div>
            </div>
        </div>
        <TdwlbConfig
            v-if="modalOpen"
            :configDetails="modalDetails"
            @changedDetails="handleConfigChange"
            @close="modalOpen = false; modalDetails = null"
        />
    </DefaultLayout>
    <template v-else>
        <div v-if="loading" class="flex flex-col items-center mt-[100px] h-full w-full">
            <h2 class="text-xl font-semibold text-gray-700 mb-8 underline">Please make sure the XetaLynk™ gateway is turned on</h2>
            <LoadingAnimation3 v-if="errorMessage === ''" size="100px" message="Requesting TDWLB Information..." />
            <div v-else class="flex flex-col items-center">
                <h2 class="text-sm text-gray-700">{{ errorMessage }}</h2>
                <button class="default-button mt-4" @click="setupListener()">Try Again</button>
            </div>
        </div>
        <div v-else class="mt-[50px] flex flex-col items-center">
            <button class="default-button mb-8" @click="modalOpen = true; modalDetails = null">Add sensor</button>
            <h2 v-if="bluetoothDevices.length === 0" class="text-xl font-semibold text-gray-700 mb-8">No bluetooth devices connected</h2>
            
            <div v-else class="grid grid-cols-1 gap-8 w-fit">
                <div v-for="device in bluetoothDevices" :key="device.serial_number" class="bg-gray-50 rounded-lg p-6">
                    <div class="flex items-center justify-between">
                        <TrashIcon v-if="loadingRemove !== device.serial_number" class="w-6 h-6 text-red-500 cursor-pointer mr-4" @click="handleRemoveDevice(device.serial_number)" />
                        <LoadingAnimation3 v-else size="20px" />
                        <h2 class="text-xl font-semibold text-gray-700">Serial Num: {{ device.serial_number }}</h2>
                    </div>
                    <img src="@/assets/images/tdwlb_pressure_sensor.png" alt="Tdwlb Sensor" class="w-[200px] cursor-pointer" @click="modalOpen = true; modalDetails = device">
                </div>
            </div>
        </div>
        <TdwlbConfig
            v-if="modalOpen"
            :configDetails="modalDetails"
            @changedDetails="handleConfigChange"
            @close="modalOpen = false; modalDetails = null"
        />
    </template>
</template>